import React, { MouseEvent, RefObject } from 'react'

import {
  CellClickArgs,
  ColumnConfig,
  ColumnPinning,
  ContentCellContext,
  Extension,
} from '../../DataGrid.types'
import { useExtensions } from '../../ExtensionsContext'
import {
  genericMemo,
  getColumnValue,
  getLeftRightVars,
  gridClassNames,
  StickyController,
} from '../../utils'
import * as S from './ContentCell.styles'

interface ContentCellProps<RowType> {
  column: ColumnConfig<RowType>
  columnIndex: number
  columnPinning: ColumnPinning
  gridId: string
  isCellInGroupedRow?: boolean
  isEvenRow: boolean
  isInteractive?: boolean
  isLastColumn?: boolean
  isLastRow?: boolean
  isSelected?: boolean
  onClick?: (cellClickArgs: CellClickArgs<RowType>) => void
  onMouseLeave: VoidFunction
  onMouseOver: (rowIndex: number) => void
  rowData: RowType
  rowIndex: number
  spanCount?: number
  stickyControllerRef: RefObject<StickyController>
  withLeftBorder?: boolean
  withRightBorder?: boolean
}

function _ContentCell<RowType>({
  column,
  columnIndex,
  columnPinning,
  gridId,
  isCellInGroupedRow,
  isEvenRow,
  isInteractive,
  isLastColumn,
  isLastRow,
  isSelected,
  onClick,
  onMouseLeave,
  onMouseOver,
  rowData,
  rowIndex,
  spanCount,
  stickyControllerRef,
  withLeftBorder,
  withRightBorder,
}: ContentCellProps<RowType>) {
  const value = getColumnValue({ columnKey: column.columnKey, rowData })

  const context: ContentCellContext<RowType> = {
    rowIndex,
    columnIndex,
    value,
    column,
    rowData,
  }

  const extensions = useExtensions(gridId) as Extension<RowType>[]

  const extensionClassnames = extensions.map(extension =>
    extension.getCustomContentCellClassName?.(context),
  )

  const getColumnPinningClassNames = () => {
    if (columnPinning === ColumnPinning.None) {
      return []
    }
    return [
      gridClassNames.pinnedColumn,
      columnPinning === ColumnPinning.Left
        ? gridClassNames.pinnedColumnLeft
        : gridClassNames.pinnedColumnRight,
    ]
  }

  const classNames = [
    gridClassNames.contentCell,
    `${gridClassNames.row}-${rowIndex}`,
    `${gridClassNames.column}-${columnIndex}`,
    isSelected ? gridClassNames.selectedRow : '',
    isLastColumn ? 'column-last' : '',
    ...getColumnPinningClassNames(),
    ...extensionClassnames,
  ].join(' ')

  const handleCellClick = (e: MouseEvent) =>
    onClick?.({
      column,
      rowIndex,
      columnIndex,
      value,
      rowData,
      groupId: column.groupId,
      target: e.currentTarget,
    })

  return (
    <S.ContentCell
      className={classNames}
      $contentAlign={column.contentAlign}
      $isEvenRow={isEvenRow}
      $isFirstColumn={columnIndex === 0}
      $isSticky={columnPinning !== ColumnPinning.None}
      $withRightBorder={withRightBorder}
      $withLeftBorder={withLeftBorder}
      $isRowSelected={isSelected}
      onClick={handleCellClick}
      onMouseOver={() => onMouseOver(rowIndex)}
      onMouseLeave={onMouseLeave}
      {...getLeftRightVars(columnPinning, stickyControllerRef, columnIndex)}
      $isInteractive={isInteractive}
      $spanCount={spanCount}
      $isCellInGroupedRow={isCellInGroupedRow}
      $noRightBorder={isLastColumn}
      $noBottomBorder={isLastRow}
    >
      {column.renderer?.({ value, rowData, rowIndex }) ?? value}
    </S.ContentCell>
  )
}

export const ContentCell = genericMemo(_ContentCell)
